import { useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHARGES, CREST_COLORS, DIVISIONS, FEMALE_NAMES, MALE_NAMES, MAX_FACE_SEED, NAME_RE, START_STATE,
  childFaceFromSeed, faceFromSeed, isMetal, randomCrest,
} from '@pk/shared';
import type { CrestSpec, FoundFamily, HouseSummary, MeResponse, Sex, TitleSummary } from '@pk/shared';
import { useFoundHouse, useWorld } from '../api/hooks';
import { useUi } from '../store';
import { Btn, Inline } from '../components/ui';
import { Heraldry, Shield } from '../components/Heraldry';
import { Portrait } from '../components/Portrait';
import { ancestorOfRank, buildLand } from '../lib/geometry';
import '../styles/found.css';

export function EndedScreen({ ended }: { ended: NonNullable<MeResponse['lastHouseEnded']> }) {
  const { t } = useTranslation();
  const dismiss = useUi((s) => s.dismissEnded);
  const { data: world } = useWorld();
  const house = world?.houses.find((h) => h.id === ended.houseId);
  return (
    <div className="splash-page"><div className="splash ended">
      <img className="splash-wordmark" src="/brand/logo-text.png" alt={t('app.name')} width={300} />
      <h1>{t('ended.title')}</h1>
      {house && <div className="crest" style={{ display: 'flex', justifyContent: 'center' }}><Heraldry seed={house.heraldrySeed} size={84} /></div>}
      <p className="lede">{t(ended.reason === 'extinct' ? 'ended.extinct' : 'ended.inactivity', { name: house?.name ?? '' })}</p>
      <p className="muted">{t('ended.when', { day: ended.day })}</p>
      <div className="ornament" aria-hidden="true">&#9670;</div>
      <p className="muted">{t('ended.next')}</p>
      <Btn variant="primary" block onClick={dismiss}>{t('ended.continue')}</Btn>
    </div></div>
  );
}

// ------------------------------------------------------------------ small helpers

const HOUSE_NAME_RE = /^[\p{L}][\p{L}\p{M}' \-]{1,31}$/u;
const validName = (n: string) => NAME_RE.test(n.trim());
const randSeed = () => Math.floor(Math.random() * MAX_FACE_SEED);
const pickOf = <T,>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const randName = (sex: Sex) => pickOf(sex === 'M' ? MALE_NAMES : FEMALE_NAMES);
const isListName = (n: string, sex: Sex) => (sex === 'M' ? MALE_NAMES : FEMALE_NAMES).includes(n);
const other = (s: Sex): Sex => (s === 'M' ? 'F' : 'M');
const colorKey = (c: string) => c.replace('#', '');
const STEPS = ['house', 'arms', 'family', 'place'] as const;

interface Member { name: string; faceSeed: number; sex: Sex }
interface Draft { head: Member; spouse: Member; children: Member[] }

function initialDraft(): Draft {
  const headSex: Sex = Math.random() < 0.8 ? 'M' : 'F';
  const kidSex: Sex[] = Array.from({ length: START_STATE.childCount }, () => (Math.random() < 0.5 ? 'M' : 'F'));
  if (kidSex.length >= 2 && kidSex.every((s) => s === kidSex[0])) kidSex[kidSex.length - 1] = other(kidSex[0]);
  const mk = (sex: Sex): Member => ({ sex, name: randName(sex), faceSeed: randSeed() });
  return { head: mk(headSex), spouse: mk(other(headSex)), children: kidSex.map(mk) };
}

/** A member whose sex changed keeps a typed name but swaps a list name for one of the new sex. */
function withSex(m: Member, sex: Sex): Member {
  if (m.sex === sex) return m;
  return { ...m, sex, name: isListName(m.name, m.sex) ? randName(sex) : m.name };
}

const Svg = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IconDice = () => <Svg><rect x="3.5" y="3.5" width="17" height="17" rx="4" /><circle cx="8.5" cy="8.5" r="1.1" fill="currentColor" /><circle cx="15.5" cy="8.5" r="1.1" fill="currentColor" /><circle cx="12" cy="12" r="1.1" fill="currentColor" /><circle cx="8.5" cy="15.5" r="1.1" fill="currentColor" /><circle cx="15.5" cy="15.5" r="1.1" fill="currentColor" /></Svg>;
const IconCheck = () => <Svg><path d="M5 12.5l4.5 4.5L19 7.5" /></Svg>;
const IconSearch = () => <Svg><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4.2-4.2" /></Svg>;
const IconChev = ({ open }: { open: boolean }) => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}><path d="M9 6l6 6-6 6" /></svg>;

// ------------------------------------------------------------------ step indicator

function StepBar({ step, reached, onGo }: { step: number; reached: number; onGo: (i: number) => void }) {
  const { t } = useTranslation();
  return (
    <nav className="found-steps" aria-label={t('found.stepOf', { n: step + 1, total: STEPS.length })}>
      {STEPS.map((k, i) => (
        <button key={k} type="button" className={`fs ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`} disabled={i > reached} onClick={() => onGo(i)} aria-current={i === step ? 'step' : undefined}>
          <span className="fs-dot">{i < step ? <IconCheck /> : i + 1}</span>
          <span className="fs-label">{t(`found.steps.${k}`)}</span>
        </button>
      ))}
    </nav>
  );
}

// ------------------------------------------------------------------ crest designer

function Swatches({ label, value, onChange }: { label: string; value: string; onChange: (c: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="pick-group">
      <h3>{label}</h3>
      <div className="swatches" role="radiogroup" aria-label={label}>
        {CREST_COLORS.map((c) => (
          <button key={c} type="button" role="radio" aria-checked={c === value} aria-label={t(`found.colors.${colorKey(c)}`)} title={t(`found.colors.${colorKey(c)}`)}
            className={`swatch ${c === value ? 'on' : ''} ${isMetal(c) ? 'metal' : ''}`} style={{ background: c }} onClick={() => onChange(c)}>
            {c === value && <IconCheck />}
          </button>
        ))}
      </div>
    </div>
  );
}

function CrestDesigner({ crest, setCrest, houseName }: { crest: CrestSpec; setCrest: (c: CrestSpec) => void; houseName: string }) {
  const { t } = useTranslation();
  const set = (p: Partial<CrestSpec>) => setCrest({ ...crest, ...p });
  const plain = crest.division === 'plain';
  const lowContrast = isMetal(crest.field) === isMetal(crest.chargeColor) || (!plain && isMetal(crest.field) === isMetal(crest.second));
  return (
    <div className="crest-designer">
      <div className="crest-stage">
        <div className="crest-frame"><Shield spec={crest} size={170} /></div>
        <p className="crest-name">{houseName.trim() ? t('found.housePreview', { name: houseName.trim() }) : t('found.housePreviewEmpty')}</p>
        <Btn small icon={<IconDice />} onClick={() => setCrest(randomCrest())}>{t('found.randomize')}</Btn>
      </div>
      <div className="crest-pickers">
        <div className="pick-group">
          <h3>{t('found.division')}</h3>
          <div className="tiles" role="radiogroup" aria-label={t('found.division')}>
            {DIVISIONS.map((d) => (
              <button key={d} type="button" role="radio" aria-checked={d === crest.division} className={`tile ${d === crest.division ? 'on' : ''}`} onClick={() => set({ division: d })}>
                <Shield spec={{ ...crest, division: d }} size={36} /><span>{t(`found.divisions.${d}`)}</span>
              </button>
            ))}
          </div>
        </div>
        <Swatches label={t('found.fieldColor')} value={crest.field} onChange={(field) => set({ field })} />
        {!plain && <Swatches label={t('found.secondColor')} value={crest.second} onChange={(second) => set({ second })} />}
        <div className="pick-group">
          <h3>{t('found.charge')}</h3>
          <div className="tiles" role="radiogroup" aria-label={t('found.charge')}>
            {CHARGES.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={c === crest.charge} className={`tile ${c === crest.charge ? 'on' : ''}`} onClick={() => set({ charge: c })}>
                <Shield spec={{ ...crest, charge: c }} size={36} /><span>{t(`found.charges.${c}`)}</span>
              </button>
            ))}
          </div>
        </div>
        <Swatches label={t('found.chargeColor')} value={crest.chargeColor} onChange={(chargeColor) => set({ chargeColor })} />
        {lowContrast && <p className="found-tip">{t('found.tinctureTip')}</p>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ family designer

function MemberCard({ role, member, genes, age, ageLabel, editSex, onName, onSex, onReroll }: {
  role: string; member: Member; genes: number[]; age: number; ageLabel: string; editSex: boolean;
  onName: (n: string) => void; onSex?: (s: Sex) => void; onReroll: () => void;
}) {
  const { t } = useTranslation();
  const bad = member.name.length > 0 && !validName(member.name);
  return (
    <article className={`member ${member.sex === 'F' ? 'f' : 'm'}`}>
      <div className="member-portrait">
        <Portrait genes={genes} sex={member.sex} age={age} size={92} />
        <button type="button" className="mini-btn" onClick={onReroll} aria-label={t('found.rollFace')} title={t('found.rollFace')}><IconDice /><span>{t('found.rollFace')}</span></button>
      </div>
      <div className="member-body">
        <div className="member-role"><b>{role}</b><span>{ageLabel}</span></div>
        <div className="name-row">
          <input className="input" value={member.name} maxLength={24} aria-label={`${role}: ${t('found.namePh')}`} aria-invalid={bad || member.name.trim() === ''} placeholder={t('found.namePh')} onChange={(e) => onName(e.target.value)} />
          <button type="button" className="icon-btn" onClick={() => onName(randName(member.sex))} aria-label={t('found.rollName')} title={t('found.rollName')}><IconDice /></button>
        </div>
        {bad && <p className="found-bad">{t('found.nameBad')}</p>}
        {editSex && onSex ? (
          <div className="sex-toggle" role="radiogroup" aria-label={`${role}: ${t('found.sex')}`}>
            {(['M', 'F'] as Sex[]).map((s) => (
              <button key={s} type="button" role="radio" aria-checked={member.sex === s} className={member.sex === s ? 'on' : ''} onClick={() => onSex(s)}>{s === 'M' ? '♂' : '♀'} {t(`found.sex${s}`)}</button>
            ))}
          </div>
        ) : (
          <div className="sex-toggle fixed"><span className="on">{member.sex === 'M' ? '♂' : '♀'} {t(`found.sex${member.sex}`)}</span></div>
        )}
      </div>
    </article>
  );
}

function FamilyDesigner({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  const { t } = useTranslation();
  const headFace = useMemo(() => faceFromSeed(draft.head.faceSeed), [draft.head.faceSeed]);
  const spouseFace = useMemo(() => faceFromSeed(draft.spouse.faceSeed), [draft.spouse.faceSeed]);
  const setHeadSex = (s: Sex) => setDraft({ ...draft, head: withSex(draft.head, s), spouse: withSex(draft.spouse, other(s)) });
  const patchChild = (i: number, p: Partial<Member>) => setDraft({ ...draft, children: draft.children.map((c, j) => (j === i ? { ...c, ...p } : c)) });
  const parentAge = t('found.ageFixed', { age: START_STATE.parentAge });
  return (
    <div className="family">
      <div className="family-parents">
        <MemberCard role={t('found.head')} member={draft.head} genes={headFace} age={START_STATE.parentAge} ageLabel={parentAge} editSex
          onName={(name) => setDraft({ ...draft, head: { ...draft.head, name } })} onSex={setHeadSex} onReroll={() => setDraft({ ...draft, head: { ...draft.head, faceSeed: randSeed() } })} />
        <MemberCard role={t('found.spouse')} member={draft.spouse} genes={spouseFace} age={START_STATE.parentAge} ageLabel={parentAge} editSex={false}
          onName={(name) => setDraft({ ...draft, spouse: { ...draft.spouse, name } })} onReroll={() => setDraft({ ...draft, spouse: { ...draft.spouse, faceSeed: randSeed() } })} />
      </div>
      <h3 className="family-sub">{t('found.children')}</h3>
      <div className="family-kids">
        {draft.children.map((c, i) => (
          <MemberCard key={i} role={t('found.child', { n: i + 1 })} member={c} genes={childFaceFromSeed(c.faceSeed, headFace, spouseFace)} age={Math.round((START_STATE.childMinAge + START_STATE.childMaxAge) / 2)}
            ageLabel={t('found.ageRange', { min: START_STATE.childMinAge, max: START_STATE.childMaxAge })} editSex
            onName={(name) => patchChild(i, { name })} onSex={(s) => setDraft({ ...draft, children: draft.children.map((k, j) => (j === i ? withSex(k, s) : k)) })} onReroll={() => patchChild(i, { faceSeed: randSeed() })} />
        ))}
      </div>
      <p className="found-note">{t('found.rest')}</p>
    </div>
  );
}

// ------------------------------------------------------------------ place chooser

interface CityRow {
  city: TitleSummary;
  county: TitleSummary | null;
  duchy: TitleSummary | null;
  kingdom: TitleSummary | null;
  house: HouseSummary | null;
  text: string;
}

function MiniMap({ titles, selectedId, onPick }: { titles: TitleSummary[]; selectedId: string; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const holdings = useMemo(() => titles.filter((x) => x.holding), [titles]);
  const land = useMemo(() => buildLand(holdings.map((h) => ({ id: h.id, x: h.holding!.x, y: h.holding!.y }))), [holdings]);
  const { x0, y0, x1, y1 } = land.bbox;
  const sel = holdings.find((h) => h.id === selectedId);
  const dotR = Math.max(4, (x1 - x0) / 130);
  return (
    <svg className="minimap" viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={sel ? t('found.mapOf', { name: sel.name }) : t('found.mapAll')}>
      <g>
        {land.cells.map((c) => (
          <path key={c.id} d={c.path} fill={holdings[c.idx].color} fillOpacity={c.id === selectedId ? 1 : sel ? 0.42 : 0.62} stroke={c.id === selectedId ? 'var(--gold-hi)' : 'rgba(0,0,0,.28)'} strokeWidth={c.id === selectedId ? dotR * 0.5 : dotR * 0.18} strokeLinejoin="round" />
        ))}
      </g>
      {holdings.filter((h) => h.rank === 'city' && h.id !== selectedId).map((h) => (
        <g key={h.id} className="mm-city" onClick={() => onPick(h.id)}>
          <circle cx={h.holding!.x} cy={h.holding!.y} r={dotR * 2.6} fill="transparent" />
          <circle cx={h.holding!.x} cy={h.holding!.y} r={dotR * 0.8} fill="#fff" fillOpacity={0.85} stroke="#000" strokeOpacity={0.45} strokeWidth={dotR * 0.2} />
        </g>
      ))}
      {sel && (
        <g className="mm-sel" pointerEvents="none">
          <circle className="mm-ping" cx={sel.holding!.x} cy={sel.holding!.y} r={dotR * 2.2} fill="none" stroke="var(--gold-hi)" strokeWidth={dotR * 0.5} />
          <circle cx={sel.holding!.x} cy={sel.holding!.y} r={dotR * 1.25} fill="var(--gold-hi)" stroke="#000" strokeWidth={dotR * 0.35} />
        </g>
      )}
    </svg>
  );
}

function CityCard({ row, on, onPick }: { row: CityRow; on: boolean; onPick: () => void }) {
  const { t } = useTranslation();
  const { city, county, house } = row;
  return (
    <button type="button" role="radio" aria-checked={on} className={`city ${on ? 'on' : ''}`} onClick={onPick}>
      <span className="city-main">
        <b>{city.name}</b>
        <span className="city-meta">{county ? county.name : t('found.noCounty')}</span>
        <span className="city-lord">{county?.holderName ? t('found.heldBy', { name: county.holderName }) : t('found.noLord')}{house ? ` · ${t('found.houseOf', { name: house.name })}` : ''}</span>
      </span>
      <span className="city-chips">
        {city.holding && <span className="chip">{t(`terrain.${city.holding.terrain}`)}</span>}
        {city.holding && <span className="chip dev">{t('found.dev', { n: city.holding.development })}</span>}
      </span>
      {on && <span className="city-check"><IconCheck /></span>}
    </button>
  );
}

function PlaceChooser({ cityId, setCityId }: { cityId: string; setCityId: (id: string) => void }) {
  const { t } = useTranslation();
  const { data: world, isLoading } = useWorld();
  const [q, setQ] = useState('');
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const rows = useMemo<CityRow[]>(() => {
    if (!world) return [];
    const byId = new Map(world.titles.map((x) => [x.id, x]));
    const houses = new Map(world.houses.map((h) => [h.id, h]));
    return world.titles.filter((x) => x.rank === 'city' && x.holding).map((city) => {
      const county = ancestorOfRank(city, 'county', byId);
      const duchy = ancestorOfRank(city, 'duchy', byId);
      const kingdom = ancestorOfRank(city, 'kingdom', byId);
      const house = county?.holderHouseId ? houses.get(county.holderHouseId) ?? null : null;
      const text = [city.name, county?.name, county?.holderName, house?.name, duchy?.name, kingdom?.name].filter(Boolean).join(' ').toLowerCase();
      return { city, county, duchy, kingdom, house, text };
    });
  }, [world]);
  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) => !needle || r.text.includes(needle));
  const groups = useMemo(() => {
    const k = new Map<string, { kingdom: TitleSummary | null; duchies: Map<string, { duchy: TitleSummary | null; rows: CityRow[] }> }>();
    for (const r of shown) {
      const kid = r.kingdom?.id ?? '';
      if (!k.has(kid)) k.set(kid, { kingdom: r.kingdom, duchies: new Map() });
      const g = k.get(kid)!;
      const did = r.duchy?.id ?? '';
      if (!g.duchies.has(did)) g.duchies.set(did, { duchy: r.duchy, rows: [] });
      g.duchies.get(did)!.rows.push(r);
    }
    const nm = (x: TitleSummary | null) => x?.name ?? '￿';
    const tid = world?.targetKingdomId ?? null;
    const first = (x: TitleSummary | null) => (tid && x?.id === tid ? 0 : 1);
    return [...k.values()].sort((a, b) => first(a.kingdom) - first(b.kingdom) || nm(a.kingdom).localeCompare(nm(b.kingdom))).map((g) => ({
      ...g,
      duchies: [...g.duchies.values()].sort((a, b) => nm(a.duchy).localeCompare(nm(b.duchy))).map((d) => ({ ...d, rows: d.rows.sort((a, b) => a.city.name.localeCompare(b.city.name)) })),
    }));
  }, [shown, world?.targetKingdomId]);
  const fill = useMemo(() => new Map((world?.kingdoms ?? []).map((k) => [k.id, k])), [world?.kingdoms]);
  const targetId = world?.targetKingdomId ?? null;
  const chosen = rows.find((r) => r.city.id === cityId) ?? null;
  const compact = rows.length > 14 && !needle;
  const isOpen = (id: string, hasSel: boolean) => closed[id] === undefined ? (!compact || hasSel) : !closed[id];

  return (
    <div className="place">
      <aside className="place-aside">
        <div className="mapcard">
          {world && <MiniMap titles={world.titles} selectedId={cityId} onPick={setCityId} />}
          {!chosen && <span className="map-badge">{t('found.surpriseMap')}</span>}
        </div>
        <div className="place-info">
          {chosen ? (
            <>
              <b className="pi-name">{chosen.city.name}</b>
              <span>{[chosen.county?.name, chosen.duchy?.name, chosen.kingdom?.name].filter(Boolean).join(' › ')}</span>
              <span>{chosen.county?.holderName ? t('found.heldBy', { name: chosen.county.holderName }) : t('found.noLord')}</span>
            </>
          ) : (
            <><b className="pi-name">{t('found.surprise')}</b><span>{t('found.surpriseText')}</span></>
          )}
          {(chosen ? !!targetId && chosen.kingdom?.id === targetId : !!targetId) && <span className="welcome-hint">{t('found.welcomeHint')}</span>}
        </div>
      </aside>
      <div className="place-list">
        <label className="search">
          <IconSearch />
          <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('found.search')} aria-label={t('found.search')} />
        </label>
        <div role="radiogroup" aria-label={t('found.steps.place')}>
          <button type="button" role="radio" aria-checked={!cityId} className={`city surprise ${!cityId ? 'on' : ''}`} onClick={() => setCityId('')}>
            <span className="surprise-ico"><IconDice /></span>
            <span className="city-main"><b>{t('found.surprise')}</b><span className="city-meta">{t('found.surpriseText')}</span></span>
            {!cityId && <span className="city-check"><IconCheck /></span>}
          </button>
          {isLoading && <p className="found-note">{t('found.loading')}</p>}
          {!isLoading && shown.length === 0 && <p className="found-note">{t('found.noCities')}</p>}
          {groups.map((g) => (
            <section key={g.kingdom?.id ?? 'none'} className="kgroup">
              <h3 className="kname">
                <span>{g.kingdom?.name ?? t('found.otherLands')}</span>
                {g.kingdom && g.kingdom.id === targetId && <span className="rec-badge">{t('found.recommended')}</span>}
                {g.kingdom && fill.has(g.kingdom.id) && (
                  <small className="kfill">{t('found.freeHouses', { human: fill.get(g.kingdom.id)!.humanHouses, total: fill.get(g.kingdom.id)!.houses })}</small>
                )}
              </h3>
              {g.kingdom && g.kingdom.id === targetId && <p className="found-note rec-note">{t('found.recommendedText')}</p>}
              {g.duchies.map((d) => {
                const did = d.duchy?.id ?? 'none';
                const hasSel = d.rows.some((r) => r.city.id === cityId);
                const open = isOpen(did, hasSel) || !!needle;
                return (
                  <div key={did} className="dgroup">
                    <button type="button" className="dhead" aria-expanded={open} onClick={() => setClosed({ ...closed, [did]: open })}>
                      <IconChev open={open} />
                      <b>{d.duchy?.name ?? t('found.otherLands')}</b>
                      <span className="count">{t('found.cityCount', { count: d.rows.length })}</span>
                    </button>
                    {open && <div className="cities">{d.rows.map((r) => <CityCard key={r.city.id} row={r} on={r.city.id === cityId} onPick={() => setCityId(r.city.id)} />)}</div>}
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ screen

export function FoundHouseScreen() {
  const { t } = useTranslation();
  const found = useFoundHouse();
  const { data: world } = useWorld();
  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [name, setName] = useState('');
  const [crest, setCrest] = useState<CrestSpec>(() => randomCrest());
  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [cityId, setCityId] = useState('');
  const err = found.error instanceof Error ? found.error.message : null;
  const houseName = name.trim();

  const nameOk = HOUSE_NAME_RE.test(houseName);
  const familyOk = [draft.head, draft.spouse, ...draft.children].every((m) => validName(m.name));
  const stepOk = [nameOk, true, familyOk, true][step];
  const go = (i: number) => { setStep(i); setReached((r) => Math.max(r, i)); document.body.scrollTo({ top: 0 }); window.scrollTo({ top: 0 }); };
  const chosen = world?.titles.find((x) => x.id === cityId);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!stepOk) return;
    if (step < STEPS.length - 1) { go(step + 1); return; }
    const family: FoundFamily = {
      head: { name: draft.head.name.trim(), sex: draft.head.sex, faceSeed: draft.head.faceSeed },
      spouse: { name: draft.spouse.name.trim(), faceSeed: draft.spouse.faceSeed },
      children: draft.children.map((c) => ({ name: c.name.trim(), sex: c.sex, faceSeed: c.faceSeed })),
    };
    found.mutate({ houseName, cityId: cityId || undefined, crest, family });
  };

  return (
    <div className="splash-page found-page">
      <form className="found" onSubmit={submit} noValidate>
        <header className="found-head">
          <img className="splash-wordmark" src="/brand/logo-text.png" alt={t('app.name')} width={220} />
          <p className="found-kicker">{t('found.title')}</p>
        </header>
        <StepBar step={step} reached={reached} onGo={go} />
        <main className="found-card" key={step}>
          <h1>{t(`found.steps.${STEPS[step]}`)}</h1>
          <p className="found-intro">{t(`found.intro.${STEPS[step]}`, { age: START_STATE.parentAge, count: START_STATE.childCount })}</p>

          {step === 0 && (
            <div className="house-step">
              <div className="house-crest"><Shield spec={crest} size={112} /></div>
              <div className="house-fields">
                <label className="field">
                  <span className="field-label">{t('found.houseName')}</span>
                  <input className="input big" autoFocus maxLength={32} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('found.houseNamePh')} aria-invalid={houseName.length > 0 && !nameOk} />
                  <span className={`field-hint ${houseName.length > 0 && !nameOk ? 'bad' : ''}`}>{houseName.length > 0 && !nameOk ? t('found.nameInvalid') : t('found.houseHint')}</span>
                </label>
                <p className="found-note">{t('found.startNote')}</p>
              </div>
            </div>
          )}
          {step === 1 && <CrestDesigner crest={crest} setCrest={setCrest} houseName={houseName} />}
          {step === 2 && <FamilyDesigner draft={draft} setDraft={setDraft} />}
          {step === 3 && (
            <>
              <PlaceChooser cityId={cityId} setCityId={setCityId} />
              <section className="summary">
                <Shield spec={crest} size={56} />
                <div>
                  <b>{t('found.housePreview', { name: houseName })}</b>
                  <span>{t('found.sumFamily', { head: draft.head.name.trim(), spouse: draft.spouse.name.trim(), kids: draft.children.map((c) => c.name.trim()).join(', ') })}</span>
                  <span>{chosen ? t('found.startsIn', { city: chosen.name }) : t('found.startsSurprise')}</span>
                </div>
              </section>
            </>
          )}
          <Inline error={err} />
        </main>
        <div className="found-nav">
          <Btn variant="quiet" onClick={() => go(step - 1)} disabled={step === 0 || found.isPending}>{t('found.back')}</Btn>
          <span className="found-count">{t('found.stepOf', { n: step + 1, total: STEPS.length })}</span>
          <Btn type="submit" variant="primary" disabled={!stepOk} busy={found.isPending}>
            {step < STEPS.length - 1 ? t('found.next') : t('found.submitNamed', { name: houseName })}
          </Btn>
        </div>
      </form>
    </div>
  );
}
